
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Calendar, Heart, Shield, MoreHorizontal, Camera, Search, Users, Brain, Store, Crown, UserCircle, MapPin, Trophy, Bone } from 'lucide-react';
import { useProfileState } from '@/hooks/useProfileState';
import { useLostPetsBadge } from '@/hooks/useLostPetsBadge';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';

// Couleurs par catégorie — inline pour garantir l'affichage
const CATEGORIES = [
  {
    title: 'Mes Animaux',
    color: '#A78BFA',
    glow: 'rgba(167,139,250,0.18)',
    gradient: 'linear-gradient(135deg, #2d1f6b 0%, #1a0f40 100%)',
    border: 'rgba(167,139,250,0.22)',
    items: [
      { path: '/gallery',      icon: Camera,  label: 'Galerie Photos', description: 'Gérer les photos de vos animaux' },
      { path: '/competitions', icon: Trophy,  label: 'Compétitions',   description: 'Performances et concours' },
      { path: '/gps-tracking', icon: MapPin,  label: 'Suivi GPS',      description: 'Localisation temps réel' },
    ],
  },
  {
    title: 'Santé & Bien-être',
    color: '#FFC107',
    glow: 'rgba(255,193,7,0.18)',
    gradient: 'linear-gradient(135deg, #3d2a00 0%, #1f1500 100%)',
    border: 'rgba(255,193,7,0.22)',
    items: [
      { path: '/calendar',   icon: Calendar, label: 'Agenda Médical', description: 'RDV vétérinaires' },
      { path: '/insurance',  icon: Shield,   label: 'Assurances',     description: 'Protection santé' },
    ],
  },
  {
    title: 'Communauté',
    color: '#34D399',
    glow: 'rgba(52,211,153,0.18)',
    gradient: 'linear-gradient(135deg, #0f3d2a 0%, #071f15 100%)',
    border: 'rgba(52,211,153,0.22)',
    items: [
      { path: '/lost-pets',    icon: Search, label: 'Animaux Perdus',   description: 'Aide et recherche' },
      { path: '/social',       icon: Users,  label: 'Forum Social',     description: 'Échanger avec la communauté' },
      { path: '/ai-assistant', icon: Brain,  label: 'Assistant IA',     description: 'Conseils personnalisés' },
    ],
  },
  {
    title: 'Services',
    color: '#F472B6',
    glow: 'rgba(244,114,182,0.18)',
    gradient: 'linear-gradient(135deg, #3d0f2a 0%, #1f0615 100%)',
    border: 'rgba(244,114,182,0.22)',
    items: [
      { path: '/breeders', icon: Bone,       label: 'Éleveurs',       description: 'Trouver des éleveurs certifiés' },
      { path: '/shop',     icon: Store,      label: 'Boutique',       description: 'Produits pour animaux' },
      { path: '/profile',  icon: UserCircle, label: 'Mon Profil',     description: 'Paramètres du compte' },
    ],
  },
] as const;

const Navigation = () => {
  const { pathname } = useLocation();
  const { userSettings } = useProfileState();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const { newCount } = useLostPetsBadge();

  const allMoreItems = CATEGORIES.flatMap(
    (cat) => cat.items as readonly { path: string; label: string; description: string }[]
  );
  const isMoreMenuActive = allMoreItems.some(item => pathname.startsWith(item.path));

  const navItemBase = "flex flex-col items-center justify-center py-2 px-2 rounded-xl transition-all duration-200 flex-1 min-w-0 relative";

  const NavItem = ({ to, icon: Icon, label, isActive }: { to: string; icon: React.ElementType; label: string; isActive: boolean }) => (
    <Link to={to} className={`${navItemBase} ${isActive ? 'text-primary' : 'text-muted-foreground'}`}>
      <div
        className="w-10 h-10 flex items-center justify-center rounded-xl mb-0.5 transition-all"
        style={isActive ? { background: 'rgba(255,193,7,0.12)', border: '1px solid rgba(255,193,7,0.25)' } : {}}
      >
        <Icon size={20} strokeWidth={isActive ? 2.5 : 1.8} />
      </div>
      <span className="text-[10px] font-bold tracking-tight leading-none">{label}</span>
    </Link>
  );

  return (
    <>
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 w-screen max-w-[100vw] backdrop-blur-xl border-t border-white/5 shadow-[0_-4px_24px_rgba(0,0,0,0.4)] pb-safe-bottom fixed-bottom-nav"
        style={{ background: 'rgba(10,10,10,0.98)' }}
      >
        <div className="flex justify-between items-center flex-nowrap min-h-[60px] px-3 w-full gap-1">
          <NavItem to="/"       icon={Home}   label="Accueil" isActive={pathname === '/'} />
          <NavItem to="/pets"   icon={Heart}  label="Animaux" isActive={pathname.startsWith('/pets')} />
          <NavItem to="/health" icon={Shield} label="Santé"   isActive={pathname.startsWith('/health')} />

          {/* Plus */}
          <Sheet open={isMoreMenuOpen} onOpenChange={setIsMoreMenuOpen}>
            <SheetTrigger asChild>
              <button className={`${navItemBase} ${isMoreMenuActive ? 'text-primary' : 'text-muted-foreground'}`}>
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl mb-0.5 transition-all"
                  style={isMoreMenuActive ? { background: 'rgba(255,193,7,0.12)', border: '1px solid rgba(255,193,7,0.25)' } : {}}
                >
                  <MoreHorizontal size={20} strokeWidth={isMoreMenuActive ? 2.5 : 1.8} />
                </div>
                <span className="text-[10px] font-bold tracking-tight leading-none">Plus</span>
                {newCount > 0 && (
                  <Badge variant="destructive" className="absolute -top-0.5 right-1 h-4 w-4 p-0 flex items-center justify-center text-[9px]">
                    {newCount}
                  </Badge>
                )}
              </button>
            </SheetTrigger>

            <SheetContent
              side="bottom"
              className="w-screen max-w-[100vw] rounded-t-3xl border-t border-white/[0.06] flex flex-col pb-safe-bottom overflow-hidden"
              style={{ background: '#0A0A0A', maxHeight: 'calc(100dvh - 5rem)' }}
            >
              {/* Header du menu */}
              <SheetHeader className="px-5 pt-2 pb-4 flex-shrink-0 border-b border-white/[0.05]">
                <SheetTitle className="text-xl font-bold text-foreground tracking-tight">Menu Principal</SheetTitle>
                <p className="text-xs text-muted-foreground/50 mt-0.5">Accédez à toutes les fonctionnalités</p>
              </SheetHeader>

              {/* Liste des catégories */}
              <div className="overflow-y-auto flex-1 px-4 py-4 space-y-5">
                {CATEGORIES.map((category) => (
                  <div key={category.title}>
                    {/* Titre de catégorie coloré */}
                    <p
                      className="text-[10px] font-bold uppercase tracking-[0.2em] mb-2 px-1"
                      style={{ color: category.color, opacity: 0.8 }}
                    >
                      {category.title}
                    </p>

                    <div className="space-y-1.5">
                      {category.items.map((item) => {
                        const isActive = pathname.startsWith(item.path);
                        const Icon = item.icon;
                        return (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setIsMoreMenuOpen(false)}
                            className="flex items-center gap-3 p-3.5 rounded-2xl transition-all active:scale-[0.98]"
                            style={isActive ? {
                              background: category.gradient,
                              border: `1px solid ${category.border}`,
                              boxShadow: `0 4px 20px ${category.glow}`,
                            } : {
                              background: 'rgba(255,255,255,0.03)',
                              border: '1px solid rgba(255,255,255,0.06)',
                            }}
                          >
                            {/* Icône colorée */}
                            <div
                              className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                              style={{
                                background: `${category.color}${isActive ? '22' : '14'}`,
                                border: `1px solid ${category.color}${isActive ? '40' : '28'}`,
                                boxShadow: isActive ? `0 0 12px ${category.glow}` : 'none',
                              }}
                            >
                              <Icon
                                size={18}
                                style={{
                                  color: category.color,
                                  filter: `drop-shadow(0 0 4px ${category.color}80)`,
                                }}
                              />
                            </div>

                            {/* Texte */}
                            <div className="flex-1 min-w-0">
                              <p className="font-bold text-[13px] text-foreground leading-tight">{item.label}</p>
                              <p className="text-[11px] text-muted-foreground/50 mt-0.5 truncate">{item.description}</p>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </>
  );
};

export default Navigation;
